import { DUPE_TOKEN_ADDRESS } from '@dupe/common/constants'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import SocialInstagramIcon from '~/src/components/SocialInstagramIcon'
import SocialTikTokIcon from '~/src/components/SocialTikTokIcon'
import SocialXIcon from '~/src/components/SocialXIcon'
import LogoIcon from '~/src/components/svg/LogoIcon'
import useInIframe from '~/src/hooks/useInIframe'

const DarkModeToggle = dynamic(
  () => import('~/src/components/DarkModeToggle'),
  { ssr: false }
)

const FurnitureMarquee = dynamic(
  () => import('~/src/components/marquee/FurnitureMarquee'),
  { ssr: false }
)

const currentYear = new Date().getFullYear()

interface FooterProps extends React.HTMLProps<HTMLDivElement> {
  hideFooter?: boolean
}

const Footer: React.FC<FooterProps> = ({ className, hideFooter, ...props }) => {
  const { inIframe, handleOnClick } = useInIframe()

  if (hideFooter) return null

  return (
    <footer
      className={clsx(
        'relative flex w-full flex-col items-center overflow-hidden bg-black text-white',
        className
      )}
      {...props}
    >
      <div className='w-full py-6'>
        <div className='mx-auto w-full flex-col items-center justify-between space-y-4 md:grid-cols-3 md:flex-row md:space-y-0 md:p-8'>
          <div className='flex flex-col items-center space-y-3'>
            <div className='flex items-center justify-center space-x-2'>
              <Link
                href='https://www.instagram.com/dupe_com'
                onClick={handleOnClick}
                target='_blank'
                className='p-2'
              >
                <SocialInstagramIcon height={18} />
              </Link>
              <Link
                href='https://www.tiktok.com/@dupedotcom'
                onClick={handleOnClick}
                target='_blank'
                className='p-2'
              >
                <SocialTikTokIcon height={18} />
              </Link>
              <Link
                href='https://x.com/dupedotcom'
                onClick={handleOnClick}
                target='_blank'
                className='p-2'
              >
                <SocialXIcon height={18} />
              </Link>
            </div>
            <div className='flex flex-wrap items-center justify-center'>
              <Link
                href='/app'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                App
              </Link>
              <Link
                href='/clearance'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Sale
              </Link>
              <Link
                href='/trade'
                target='_blank'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Trade
              </Link>
              <Link
                href='/blog'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Blog
              </Link>
              <Link
                href='/products/furniture'
                target='_blank'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Products
              </Link>
            </div>
            <div className='flex flex-wrap items-center justify-center'>
              <Link
                href='/about'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                About
              </Link>
              <Link
                href='/press'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Press
              </Link>
              <Link
                href='/contact'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                Contact
              </Link>
              <Link
                href='/faq'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                FAQ
              </Link>
              <Link
                href='/$dupe'
                onClick={handleOnClick}
                className='font-cta px-2 py-2 text-sm uppercase hover:underline'
              >
                $DUPE
              </Link>
            </div>
            <div className='flex items-center justify-center space-x-3'>
              <div className='flex items-center'>
                <Link
                  href='/legal/privacy'
                  onClick={handleOnClick}
                  target='_blank'
                  className='font-cta px-2 py-2 text-sm uppercase hover:underline'
                >
                  Privacy
                </Link>
                <Link
                  href='/legal/terms'
                  onClick={handleOnClick}
                  target='_blank'
                  className='font-cta px-2 py-2 text-sm uppercase hover:underline'
                >
                  Terms
                </Link>
                <div className='font-cta px-2 py-2 text-sm uppercase'>
                  © {currentYear} Dupe
                </div>
              </div>
            </div>
            <div className='mx-2 flex justify-center'>
              <Link
                href='/$dupe'
                className='inline-flex flex-wrap rounded-full border border-white/50 px-2 py-2 text-xs opacity-70 transition-colors hover:bg-white/10 hover:opacity-100 md:space-x-1 md:px-4 md:text-sm'
              >
                <div className='whitespace-nowrap'>$DUPE! CA:</div>
                <div>{DUPE_TOKEN_ADDRESS}</div>
              </Link>
            </div>
            <div className='flex justify-center'>
              {!inIframe && <DarkModeToggle />}
            </div>
          </div>
        </div>
      </div>

      <div className='relative py-8 md:py-16'>
        <div className='absolute inset-0 flex items-center justify-center'>
          <LogoIcon
            className='mt-[100px] h-auto w-[300px] md:mt-[270px] md:w-[700px]'
            color='white'
          />
        </div>
        <FurnitureMarquee />
      </div>
    </footer>
  )
}

export default Footer
